import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { paymentType } from '@ourbranch/lookups';

import { awsDateToStringDate, currencyFormatter } from 'core/helpers/formatters';

import useStyles from './price-change-panel.styles';

const signedCurrency = (amount) => `${amount < 0 ? '−' : '+'}${currencyFormatter(Math.abs(amount))}`;

// The drawer's preview phase, after the prototype: term price before and after, what the change adds or takes off
// the rest of this term, the next installment on a payment plan, then policy-mod's own billing description (which
// says whether we charge or refund, and how much).
const PricePanel = ({ preview }) => {
  const classes = useStyles();
  const startDate = preview.changeDetails?.effectiveDate;
  const billing = preview.structuredBillingDescription;
  const hasPrices = Number.isFinite(billing?.oldPrice) && Number.isFinite(billing?.newPrice);
  const priceChange = hasPrices ? Math.round((billing.newPrice - billing.oldPrice) * 100) / 100 : null;
  const nextPayment = billing?.paymentType !== paymentType.OneTime ? billing?.nextPayments?.[0] : null;

  return (
    <section className={classes.panel} aria-labelledby="price-change-title">
      <div className={classes.heading}>
        <h3 id="price-change-title" className={classes.title}>
          Price change
        </h3>
        {startDate && <span className={classes.starts}>Starts {awsDateToStringDate(startDate)}</span>}
      </div>
      {hasPrices && (
        <dl className={classes.rows}>
          <dt>Term price</dt>
          <dd>
            <s className={classes.was}>{currencyFormatter(billing.oldPrice)}</s>
            <strong>{currencyFormatter(billing.newPrice)}</strong>
          </dd>
          <dt>Rest of this term</dt>
          <dd>
            <strong>{priceChange === 0 ? 'No change' : signedCurrency(priceChange)}</strong>
          </dd>
          {nextPayment && (
            <>
              <dt>Next payment</dt>
              <dd>
                <strong>{currencyFormatter(nextPayment.total ?? nextPayment.remainingMonthly)}</strong>
                {nextPayment.date && ` on ${awsDateToStringDate(nextPayment.date)}`}
                {billing.remainingPayments > 0 &&
                  `, ${billing.remainingPayments} ${billing.remainingPayments === 1 ? 'payment' : 'payments'} left`}
              </dd>
            </>
          )}
        </dl>
      )}
      {preview.billingDescription && (
        <p
          className={classes.description}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify, as in the Preview modal
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.billingDescription) }}
        />
      )}
    </section>
  );
};

PricePanel.propTypes = {
  preview: PropTypes.shape({
    billingDescription: PropTypes.string,
    changeDetails: PropTypes.shape({ effectiveDate: PropTypes.string }),
    structuredBillingDescription: PropTypes.shape({
      oldPrice: PropTypes.number,
      newPrice: PropTypes.number,
      paymentType: PropTypes.string,
      remainingPayments: PropTypes.number,
      nextPayments: PropTypes.arrayOf(
        PropTypes.shape({ date: PropTypes.string, remainingMonthly: PropTypes.number, total: PropTypes.number })
      )
    })
  }).isRequired
};

export default PricePanel;
